/**
 * The ingredient converter ("Convert Pack Size to UoM used in Recipes",
 * plans/uom/project/IngredientConversions.dc.html): one column each for a
 * count, a weight and a volume per case, and rows for what a case holds, its
 * yield, density and the unit recipes use. This file is the state and the
 * arithmetic; `PackSizeConverter` renders it.
 *
 * Every unit comes from the `uoms` table and every dropdown is a filter on its
 * flags ("UoM Reference Tables" §3). Sizes are saved in base units — each,
 * grams, millilitres — whatever unit they were typed in.
 */

import { t } from "@lingui/core/macro";
import type { IngredientConversion, Uom, UpsertIngredientConversionInput } from "@/types/material-conversion";
import { parsePackSize, type PackContainerOption, type PackSizeParseResult } from "./pack-size-parser";
import { formatNumber } from "./conversion-calc";

export type ConverterColumn = "EACH" | "WEIGHT" | "LIQUID";

export const CONVERTER_COLUMNS: ConverterColumn[] = ["EACH", "WEIGHT", "LIQUID"];

/**
 * The highest yield a column may record, in percent; the API refuses more.
 * Above 100 is real: dry rice or pasta gains weight when cooked.
 */
export const MAX_YIELD_PCT = 400;

export interface ColumnState {
  /** What a case holds, in `unitCode`, as typed. */
  perCase: string;
  /** A pack unit of the column's type. The count column is always in each. */
  unitCode: string;
  yieldPct: string;
  /** Empty means "the column's default recipe unit". */
  recipeUomCode: string;
  /** Opened with "+ Add …" and not filled in yet. */
  open: boolean;
}

export interface ConverterState {
  packText: string;
  supplierId: number | null;
  supplierCode: string;
  casePrice: string;
  /** The cook's answer to "is this OZ weight or liquid?", or null while unasked. */
  ozAs: "oz" | "floz" | null;
  columns: Record<ConverterColumn, ColumnState>;
  /** The volume filled and weighed to find density ("1 cup = 160 g"). */
  densityRefCode: string;
  densityGrams: string;
}

/** Codes the columns fall back to, when the table has them. */
const DEFAULT_PACK_UNIT: Record<ConverterColumn, string> = { EACH: "ea", WEIGHT: "lb", LIQUID: "gal" };
const DEFAULT_RECIPE_UNIT: Record<ConverterColumn, string> = { EACH: "ea", WEIGHT: "oz", LIQUID: "floz" };
const DEFAULT_DENSITY_REF = "cup";

/** Short names for the few units whose table label is long ("fluid ounce"). */
const SHORT_LABELS: Record<string, string> = { floz: "fl oz", tbsp: "Tbsp", ml: "mL", l: "L", ea: "each", dz: "dozen", pt: "pint" };

export function uomLabel(uom: Pick<Uom, "code">): string {
  return SHORT_LABELS[uom.code] ?? uom.code;
}

function emptyColumn(column: ConverterColumn): ColumnState {
  return { perCase: "", unitCode: DEFAULT_PACK_UNIT[column], yieldPct: "", recipeUomCode: "", open: false };
}

export function emptyConverterState(): ConverterState {
  return {
    packText: "",
    supplierId: null,
    supplierCode: "",
    casePrice: "",
    ozAs: null,
    columns: { EACH: emptyColumn("EACH"), WEIGHT: emptyColumn("WEIGHT"), LIQUID: emptyColumn("LIQUID") },
    densityRefCode: DEFAULT_DENSITY_REF,
    densityGrams: "",
  };
}

// ── Dropdown filters ("UoM Reference Tables" §3) ─────────────────────────────

function byOrder(a: Uom, b: Uom) {
  return a.sortOrder - b.sortOrder;
}

export interface ConverterUnitOptions {
  /** Per case · Weighted: type = weight AND pack. */
  weightPack: Uom[];
  /** Per case · Liquid: type = liquid AND pack. */
  liquidPack: Uom[];
  /** Density · Weighted: type = liquid AND density_ref. */
  densityRef: Uom[];
  /** UoM in recipes · Weighted, weight group: type = weight AND recipe. */
  weightRecipe: Uom[];
  /** UoM in recipes · Weighted, volume group, and · Liquid: type = liquid AND recipe. */
  liquidRecipe: Uom[];
  /** UoM in recipes · Each: type = each AND recipe. */
  eachRecipe: Uom[];
}

export function converterUnitOptions(uoms: Uom[]): ConverterUnitOptions {
  const pick = (keep: (u: Uom) => boolean) => uoms.filter(keep).sort(byOrder);
  return {
    weightPack: pick((u) => u.type === "WEIGHT" && u.isPack),
    liquidPack: pick((u) => u.type === "LIQUID" && u.isPack),
    densityRef: pick((u) => u.type === "LIQUID" && u.isDensityRef),
    weightRecipe: pick((u) => u.type === "WEIGHT" && u.isRecipe),
    liquidRecipe: pick((u) => u.type === "LIQUID" && u.isRecipe),
    eachRecipe: pick((u) => u.type === "EACH" && u.isRecipe),
  };
}

// ── Reading the pack size into a column ──────────────────────────────────────

export interface PackFill {
  column: ConverterColumn;
  perCase: string;
  unitCode: string;
}

/**
 * What the pack size says a case holds, placed in the column of its unit's
 * type: "4/5 LB BG" is 20 lb in the weighted column, "6/#10 CAN" is 636 fl oz
 * in the liquid one. Null until the text reads cleanly (a bare "OZ" needs the
 * weight-or-liquid answer first).
 */
export function packFillFor(parse: PackSizeParseResult, uoms: Uom[]): PackFill | null {
  if (parse.status !== "ok" || parse.count == null || parse.size == null || !parse.uomCode) return null;
  const uom = uoms.find((u) => u.code === parse.uomCode);
  if (!uom) return null;
  const amount = parse.count * parse.size;
  if (uom.type === "EACH") {
    return { column: "EACH", perCase: formatPlain(amount * uom.toBase), unitCode: "ea" };
  }
  return { column: uom.type, perCase: formatPlain(amount), unitCode: uom.code };
}

export function parseConverterPack(
  state: ConverterState,
  uoms: Uom[],
  containers: PackContainerOption[],
): PackSizeParseResult {
  return parsePackSize(state.packText, uoms, containers, state.ozAs);
}

/**
 * The new state after typing a pack size: a clean read fills its column. A
 * column the pack size filled and nobody has touched since is emptied when the
 * pack size stops saying it — typing "50 lb" passes through "50 l", which is
 * not 50 litres.
 */
export function withPackText(
  state: ConverterState,
  packText: string,
  uoms: Uom[],
  containers: PackContainerOption[],
): ConverterState {
  const before = packFillFor(parseConverterPack(state, uoms, containers), uoms);
  const isBareOz = /\bOZ\b/i.test(packText) && !/FL\s?OZ/i.test(packText);
  const next = withPackFill({ ...state, packText, ozAs: isBareOz ? state.ozAs : null }, uoms, containers);
  const after = packFillFor(parseConverterPack(next, uoms, containers), uoms);
  if (!before || after?.column === before.column) return next;
  const stale = next.columns[before.column];
  if (stale.perCase !== before.perCase || stale.unitCode !== before.unitCode) return next;
  return { ...next, columns: { ...next.columns, [before.column]: emptyColumn(before.column) } };
}

/** The new state after answering "is this OZ weight or liquid?". */
export function withOzAnswer(
  state: ConverterState,
  ozAs: "oz" | "floz",
  uoms: Uom[],
  containers: PackContainerOption[],
): ConverterState {
  return withPackFill({ ...state, ozAs }, uoms, containers);
}

function withPackFill(state: ConverterState, uoms: Uom[], containers: PackContainerOption[]): ConverterState {
  const fill = packFillFor(parseConverterPack(state, uoms, containers), uoms);
  if (!fill) return state;
  const current = state.columns[fill.column];
  return {
    ...state,
    columns: {
      ...state.columns,
      [fill.column]: { ...current, perCase: fill.perCase, unitCode: fill.unitCode, yieldPct: current.yieldPct || "100" },
    },
  };
}

// ── Per-column arithmetic ────────────────────────────────────────────────────

function positive(value: string): number | null {
  const n = parseFloat(value.replace(/[$,]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function clampYield(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.min(MAX_YIELD_PCT, Math.max(1, n)) : 100;
}

export function isColumnSet(state: ConverterState, column: ConverterColumn): boolean {
  return positive(state.columns[column].perCase) !== null;
}

/** Grams per millilitre from "1 {ref} = {grams} g", or null until both are there. */
export function densityGPerMl(state: ConverterState, uoms: Uom[]): number | null {
  const grams = positive(state.densityGrams);
  const ref = uoms.find((u) => u.code === state.densityRefCode);
  return grams !== null && ref ? grams / ref.toBase : null;
}

export interface ConversionStep {
  formula: string;
  result: string;
}

export interface ColumnResult {
  set: boolean;
  /** What a case holds in the column's base unit (each, g, mL), before yield. */
  basePerCase: number | null;
  usableBase: number | null;
  recipeUom: Uom | null;
  /** How many recipe units one case makes, after yield. */
  perCaseInRecipeUnit: number | null;
  costPerRecipeUnit: number | null;
  steps: ConversionStep[];
}

const BASE_LABEL: Record<ConverterColumn, string> = { EACH: "each", WEIGHT: "g", LIQUID: "mL" };

/** The recipe units a column may use right now; volume in the weighted column needs a density. */
export function recipeUnitChoices(
  column: ConverterColumn,
  options: ConverterUnitOptions,
  hasDensity: boolean,
): { uom: Uom; disabled: boolean }[] {
  if (column === "EACH") return options.eachRecipe.map((uom) => ({ uom, disabled: false }));
  if (column === "LIQUID") return options.liquidRecipe.map((uom) => ({ uom, disabled: false }));
  return [
    ...options.weightRecipe.map((uom) => ({ uom, disabled: false })),
    ...options.liquidRecipe.map((uom) => ({ uom, disabled: !hasDensity })),
  ];
}

/** The column's chosen recipe unit if it is still allowed, else its default, else the first allowed. */
export function effectiveRecipeUom(
  state: ConverterState,
  column: ConverterColumn,
  options: ConverterUnitOptions,
  hasDensity: boolean,
): Uom | null {
  const allowed = recipeUnitChoices(column, options, hasDensity).filter((c) => !c.disabled).map((c) => c.uom);
  const chosen = state.columns[column].recipeUomCode;
  return (
    allowed.find((u) => u.code === chosen) ??
    allowed.find((u) => u.code === DEFAULT_RECIPE_UNIT[column]) ??
    allowed[0] ??
    null
  );
}

export function columnResult(
  state: ConverterState,
  column: ConverterColumn,
  uoms: Uom[],
  options: ConverterUnitOptions,
): ColumnResult {
  const col = state.columns[column];
  const amount = positive(col.perCase);
  const density = densityGPerMl(state, uoms);
  const recipeUom = effectiveRecipeUom(state, column, options, column === "WEIGHT" && density !== null);
  const empty: ColumnResult = {
    set: false,
    basePerCase: null,
    usableBase: null,
    recipeUom,
    perCaseInRecipeUnit: null,
    costPerRecipeUnit: null,
    steps: [],
  };
  if (amount === null) return empty;

  const packUnit = column === "EACH" ? null : uoms.find((u) => u.code === col.unitCode);
  if (column !== "EACH" && !packUnit) return empty;
  const base = packUnit ? amount * packUnit.toBase : amount;
  const yieldPct = clampYield(col.yieldPct);
  const usable = (base * yieldPct) / 100;
  const baseLabel = BASE_LABEL[column];

  const steps: ConversionStep[] = [];
  if (packUnit && packUnit.toBase !== 1) {
    steps.push({
      formula: `${formatNumber(amount)} ${uomLabel(packUnit)} × ${formatNumber(packUnit.toBase)}`,
      result: `${formatNumber(base, 0)} ${baseLabel}`,
    });
  }
  if (yieldPct !== 100) {
    const yieldText = formatNumber(yieldPct, 1);
    steps.push({ formula: t`× ${yieldText}% yield`, result: `${formatNumber(usable, 0)} ${baseLabel}` });
  }

  let inRecipeUnit: number | null = null;
  if (recipeUom) {
    const toBaseText = formatNumber(recipeUom.toBase);
    const recipeUnitLabel = uomLabel(recipeUom);
    const crossesToVolume = column === "WEIGHT" && recipeUom.type === "LIQUID";
    if (crossesToVolume && density !== null) {
      const ml = usable / density;
      const densityText = formatNumber(density, 3);
      steps.push({ formula: t`÷ ${densityText} g per mL`, result: `${formatNumber(ml, 0)} mL` });
      inRecipeUnit = ml / recipeUom.toBase;
      if (recipeUom.toBase !== 1) {
        steps.push({
          formula: t`÷ ${toBaseText} mL per ${recipeUnitLabel}`,
          result: `${formatNumber(inRecipeUnit, 1)} ${uomLabel(recipeUom)}`,
        });
      }
    } else if (!crossesToVolume) {
      inRecipeUnit = usable / recipeUom.toBase;
      if (recipeUom.toBase !== 1) {
        steps.push({
          formula: t`÷ ${toBaseText} ${baseLabel} per ${recipeUnitLabel}`,
          result: `${formatNumber(inRecipeUnit, 1)} ${uomLabel(recipeUom)}`,
        });
      }
    }
  }

  const price = parseFloat(state.casePrice.replace(/[$,]/g, ""));
  const costPerRecipeUnit =
    inRecipeUnit !== null && inRecipeUnit > 0 && Number.isFinite(price) && price >= 0 ? price / inRecipeUnit : null;

  return {
    set: true,
    basePerCase: base,
    usableBase: usable,
    recipeUom,
    perCaseInRecipeUnit: inRecipeUnit,
    costPerRecipeUnit,
    steps,
  };
}

/** "1 case = 128 cup": a small count keeps two decimals, a big one none. */
export function formatCaseResult(perCase: number, uom: Uom): string {
  const amount = formatNumber(perCase, perCase < 10 ? 2 : 0);
  const unitLabel = uomLabel(uom);
  return t`1 case = ${amount} ${unitLabel}`;
}

// ── Save and load ────────────────────────────────────────────────────────────

/** Why the grid can't be saved yet, or null when it can. */
export function converterProblem(state: ConverterState): "EMPTY" | "YIELD" | "PRICE" | null {
  if (!CONVERTER_COLUMNS.some((c) => isColumnSet(state, c))) return "EMPTY";
  for (const column of CONVERTER_COLUMNS) {
    const value = state.columns[column].yieldPct.trim();
    if (!isColumnSet(state, column) || value === "") continue;
    const n = Number(value);
    if (!(n >= 1 && n <= MAX_YIELD_PCT)) return "YIELD";
  }
  const price = state.casePrice.trim().replace(/[$,]/g, "");
  if (price !== "" && !(Number(price) >= 0)) return "PRICE";
  return null;
}

export function hasConverterInput(state: ConverterState): boolean {
  return state.packText.trim() !== "" || state.casePrice.trim() !== "" || CONVERTER_COLUMNS.some((c) => isColumnSet(state, c));
}

/** The body for `POST`/`PATCH /materials/:id/conversions`; columns left empty are cleared. */
export function converterPayload(state: ConverterState, uoms: Uom[]): UpsertIngredientConversionInput {
  const options = converterUnitOptions(uoms);
  const density = isColumnSet(state, "WEIGHT") ? densityGPerMl(state, uoms) : null;
  const result = (column: ConverterColumn) => columnResult(state, column, uoms, options);
  const each = result("EACH");
  const weight = result("WEIGHT");
  const liquid = result("LIQUID");
  const yieldOf = (column: ConverterColumn) => clampYield(state.columns[column].yieldPct);
  const price = state.casePrice.trim().replace(/[$,]/g, "");

  return {
    supplierId: state.supplierId,
    supplierCode: state.supplierCode.trim() || null,
    packSizeText: state.packText.trim() || null,
    casePrice: price === "" ? null : Number(price),
    eachPerCase: each.set ? each.basePerCase : null,
    eachYieldPct: each.set ? yieldOf("EACH") : null,
    eachRecipeUomCode: each.set ? (each.recipeUom?.code ?? null) : null,
    weightPerCaseG: weight.set ? weight.basePerCase : null,
    weightYieldPct: weight.set ? yieldOf("WEIGHT") : null,
    weightRecipeUomCode: weight.set ? (weight.recipeUom?.code ?? null) : null,
    densityGPerMl: density,
    liquidPerCaseMl: liquid.set ? liquid.basePerCase : null,
    liquidYieldPct: liquid.set ? yieldOf("LIQUID") : null,
    liquidRecipeUomCode: liquid.set ? (liquid.recipeUom?.code ?? null) : null,
  };
}

/**
 * What a case holds as the ingredient's own pack size ("1 case = 85 each"),
 * which costing falls back on when no supplier item is priced. Taken from the
 * column the pack size filled, else the first column that is set, in the unit
 * it was entered in. Null when no column is set.
 */
export function legacyPackFor(
  state: ConverterState,
  uoms: Uom[],
  containers: PackContainerOption[],
): { packQuantity: number; packUom: string } | null {
  const fill = packFillFor(parseConverterPack(state, uoms, containers), uoms);
  const order: ConverterColumn[] = fill ? [fill.column, ...CONVERTER_COLUMNS] : CONVERTER_COLUMNS;
  const column = order.find((c) => isColumnSet(state, c));
  if (!column) return null;
  const col = state.columns[column];
  // The ingredient's units are the costing table's names, where a count is "each".
  return { packQuantity: Number(positive(col.perCase)), packUom: column === "EACH" ? "each" : col.unitCode };
}

/** A number as a person would type it: no grouping, at most four decimals. */
function formatPlain(value: number): string {
  return String(parseFloat(value.toFixed(4)));
}

/**
 * The pack unit to show a stored base amount in: the pack size's own unit when
 * it is of this type, otherwise the first unit that gives a round number
 * (22,679.6 g reads back as 50 lb), otherwise the base unit.
 */
function displayUnitFor(base: number, candidates: Uom[], preferred: string | null, fallback: string): Uom | null {
  const preferredUom = candidates.find((u) => u.code === preferred);
  if (preferredUom) return preferredUom;
  const largestFirst = [...candidates].sort((a, b) => b.toBase - a.toBase);
  const round = largestFirst.find((u) => {
    const n = base / u.toBase;
    return n >= 1 && Math.abs(n * 100 - Math.round(n * 100)) < 0.01;
  });
  return round ?? candidates.find((u) => u.code === fallback) ?? candidates[0] ?? null;
}

/**
 * How the cook answered "is this OZ weight or liquid?" for a saved item. A case
 * can hold both a weight and a volume, so the answer is whichever stored amount
 * the pack size actually adds up to; only when neither matches does the column
 * that is filled decide.
 */
function inferOzAnswer(
  conversion: IngredientConversion,
  uoms: Uom[],
  containers: PackContainerOption[],
): "oz" | "floz" | null {
  const text = conversion.packSizeText ?? "";
  const matches = (code: "oz" | "floz", stored: number | null) => {
    if (stored == null) return false;
    const parse = parsePackSize(text, uoms, containers, code);
    const uom = uoms.find((u) => u.code === code);
    if (parse.status !== "ok" || parse.count == null || parse.size == null || !uom) return false;
    const base = parse.count * parse.size * uom.toBase;
    return Math.abs(base - stored) <= Math.max(0.5, stored * 0.001);
  };
  if (matches("oz", conversion.weightPerCaseG)) return "oz";
  if (matches("floz", conversion.liquidPerCaseMl)) return "floz";
  return conversion.weightPerCaseG != null ? "oz" : conversion.liquidPerCaseMl != null ? "floz" : null;
}

export function converterStateFromConversion(
  conversion: IngredientConversion,
  uoms: Uom[],
  containers: PackContainerOption[],
): ConverterState {
  const options = converterUnitOptions(uoms);
  const state = emptyConverterState();
  const ozAs = inferOzAnswer(conversion, uoms, containers);
  const parse = parsePackSize(conversion.packSizeText ?? "", uoms, containers, ozAs);
  const packUnit = parse.status === "ok" ? parse.uomCode : null;

  const fromBase = (
    column: ConverterColumn,
    base: number | null,
    yieldPct: number | null,
    recipeUomCode: string | null,
    candidates: Uom[] | null,
  ): ColumnState => {
    if (base == null) return { ...emptyColumn(column), recipeUomCode: recipeUomCode ?? "" };
    const fallback = DEFAULT_PACK_UNIT[column];
    const unit = candidates ? displayUnitFor(base, candidates, packUnit, fallback) : null;
    return {
      perCase: formatPlain(unit ? base / unit.toBase : base),
      unitCode: unit?.code ?? fallback,
      yieldPct: String(yieldPct ?? 100),
      recipeUomCode: recipeUomCode ?? "",
      open: false,
    };
  };

  const density = conversion.densityGPerMl;
  const ref = uoms.find((u) => u.code === DEFAULT_DENSITY_REF) ?? options.densityRef[0];

  return {
    ...state,
    packText: conversion.packSizeText ?? "",
    supplierId: conversion.supplierId,
    supplierCode: conversion.supplierCode ?? "",
    casePrice: conversion.casePrice != null ? String(conversion.casePrice) : "",
    ozAs,
    columns: {
      EACH: fromBase("EACH", conversion.eachPerCase, conversion.eachYieldPct, conversion.eachRecipeUomCode, null),
      WEIGHT: fromBase("WEIGHT", conversion.weightPerCaseG, conversion.weightYieldPct, conversion.weightRecipeUomCode, options.weightPack),
      LIQUID: fromBase("LIQUID", conversion.liquidPerCaseMl, conversion.liquidYieldPct, conversion.liquidRecipeUomCode, options.liquidPack),
    },
    densityRefCode: ref?.code ?? DEFAULT_DENSITY_REF,
    densityGrams: density != null && ref ? formatPlain(Math.round(density * ref.toBase * 10) / 10) : "",
  };
}

/** Usable cost of one base unit in the first column both sides have, for the price-sanity check. */
export function usableCostPerBase(
  source: { casePrice: number | null; weightPerCaseG: number | null; weightYieldPct: number | null; liquidPerCaseMl: number | null; liquidYieldPct: number | null; eachPerCase: number | null; eachYieldPct: number | null },
  column: ConverterColumn,
): number | null {
  const [amount, yieldPct] =
    column === "WEIGHT" ? [source.weightPerCaseG, source.weightYieldPct]
    : column === "LIQUID" ? [source.liquidPerCaseMl, source.liquidYieldPct]
    : [source.eachPerCase, source.eachYieldPct];
  if (source.casePrice == null || source.casePrice <= 0 || amount == null || amount <= 0) return null;
  const pct = Math.min(MAX_YIELD_PCT, Math.max(1, yieldPct ?? 100));
  return source.casePrice / ((amount * pct) / 100);
}
